import { useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import {
  Drawer,
  Button,
  Space,
  Typography,
  Tooltip,
  message,
  Tag,
  Empty,
  Skeleton,
  Modal,
  Avatar,
  Alert,
  Segmented,
} from 'antd';
import {
  HistoryOutlined,
  SwapOutlined,
  RollbackOutlined,
  CheckOutlined,
  CloseOutlined,
  ClockCircleOutlined,
  PlusOutlined,
  MinusOutlined,
  EyeOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useStore } from '@/stores';
import { colors, typography } from '@/theme/tokens';
import {
  listSkillHistory,
  getSkillHistoryDetail,
  restoreSkillHistory,
  type SkillHistoryRow,
} from '@/api/skillHistory';
import { getSkillById } from '@/api/skills';
import type { AiSkill } from '@/api/types';
import { computeLineDiff, type DiffSummary, type DiffRow } from '../lineDiff';

dayjs.extend(relativeTime);

const { Text, Title } = Typography;

// ─────────────────────────────────────────────────────────────────────────────
// SkillHistoryDrawer — nurse-friendly version history for an ai.AiSkill.
//
// Mirrors InstructionHistoryDrawer (agent instructions) so authors switching
// between the two surfaces see consistent affordances + copy:
//   - Timeline of cards in plain English: "3 hours ago by Lisa Walton —
//     added 320 characters."
//   - No jargon: "compare", "use this version", "go back" — not "diff",
//     "revert", "commit".
//   - Two compare modes:
//        a) ONE-CLICK "Compare with current" on every row.
//        b) MULTI-SELECT to compare any two saved versions.
//   - Side-by-side line diff with synchronized scroll + green/red highlights.
//
// SKILL-SPECIFIC vs INSTRUCTION:
//   - A skill has TWO content fields (Description + Instructions), not one.
//     The compare pane carries a small Segmented control to flip between
//     them so authors can see both deltas without a second click.
//   - Restored fields = Instructions + Description + Category + Version.
//     Name is intentionally never restored (it's the business key; renaming
//     retroactively would break ai.AiAgentSkill links).
// ─────────────────────────────────────────────────────────────────────────────

interface Props {
  /** Skill to inspect. When null the drawer stays closed. */
  skillId: number | null;
  /** Optional fallback for the header label while the current skill loads. */
  skillName?: string | null;
  onClose: () => void;
  /**
   * Called after a successful "Use this version" so the parent can refresh
   * its row + re-fetch the SkillContentDrawer body. Optional.
   */
  onRestored?: (next: { instructions: string; updatedDate: string; version?: string | null }) => void;
}

type PaneKind = 'list' | 'compare';
type FieldKey = 'instructions' | 'description';

interface CompareState {
  /** Older side. */
  left: { label: string; sublabel: string; instructions: string; description: string };
  /** Newer side. */
  right: { label: string; sublabel: string; instructions: string; description: string };
}

export const SkillHistoryDrawer = observer(function SkillHistoryDrawer({
  skillId,
  skillName,
  onClose,
  onRestored,
}: Props) {
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';

  const open = skillId != null;

  const [currentSkill, setCurrentSkill] = useState<AiSkill | null>(null);
  const [pane, setPane] = useState<PaneKind>('list');
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<SkillHistoryRow[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [compare, setCompare] = useState<CompareState | null>(null);
  const [field, setField] = useState<FieldKey>('instructions');
  const [restoringId, setRestoringId] = useState<number | null>(null);

  // ── Reset on open ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!open || skillId == null) {
      // Clear so a re-open on a different skill doesn't flash stale rows.
      setRows([]);
      setCurrentSkill(null);
      setLoadError(null);
      setSelected(new Set());
      setCompare(null);
      setPane('list');
      setField('instructions');
      return;
    }
    setPane('list');
    setField('instructions');
    setSelected(new Set());
    setCompare(null);
    void loadAll(skillId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, skillId]);

  async function loadAll(id: number) {
    setLoading(true);
    setLoadError(null);
    try {
      const [page, current] = await Promise.all([
        listSkillHistory(id, 1, 50),
        getSkillById(id),
      ]);
      setRows(page.data ?? []);
      setCurrentSkill(current);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not load history.';
      setLoadError(msg);
      setRows([]);
      void message.error(msg);
    } finally {
      setLoading(false);
    }
  }

  // ── Char-count delta per row vs newer neighbor (or current body for newest) ─
  // The Instructions body is the dominant signal — that's what authors are
  // editing day-to-day. Description deltas are usually small.
  const deltaFor = useMemo(() => {
    const map = new Map<number, number>();
    const currentLen = currentSkill?.instructions?.length ?? 0;
    for (let i = 0; i < rows.length; i++) {
      const me = rows[i];
      const newerLen = i === 0 ? currentLen : rows[i - 1].charCount;
      map.set(me.id, newerLen - me.charCount);
    }
    return map;
  }, [rows, currentSkill]);

  // ── 1-click compare against the live current body ────────────────────
  async function compareWithCurrent(row: SkillHistoryRow) {
    if (skillId == null || !currentSkill) return;
    setLoading(true);
    try {
      const detail = await getSkillHistoryDetail(skillId, row.id);
      setCompare({
        left: {
          label: friendlyDate(detail.changedDate),
          sublabel: editorLabel(detail) + ' • ' + numberWithCommas(detail.charCount) + ' characters',
          instructions: detail.instructions ?? '',
          description: detail.description ?? '',
        },
        right: {
          label: 'Current version',
          sublabel:
            'What this skill uses right now • ' +
            numberWithCommas((currentSkill.instructions ?? '').length) +
            ' characters',
          instructions: currentSkill.instructions ?? '',
          description: currentSkill.description ?? '',
        },
      });
      setPane('compare');
    } catch (err) {
      void message.error(err instanceof Error ? err.message : 'Could not load that version.');
    } finally {
      setLoading(false);
    }
  }

  // ── Multi-select compare two history rows ────────────────────────────
  async function compareSelectedTwo() {
    if (skillId == null || selected.size !== 2) return;
    const ids = Array.from(selected);
    // Order older → newer based on list index (rows are newest-first).
    const idxA = rows.findIndex((r) => r.id === ids[0]);
    const idxB = rows.findIndex((r) => r.id === ids[1]);
    const olderId = idxA > idxB ? ids[0] : ids[1];
    const newerId = olderId === ids[0] ? ids[1] : ids[0];

    setLoading(true);
    try {
      const [olderDetail, newerDetail] = await Promise.all([
        getSkillHistoryDetail(skillId, olderId),
        getSkillHistoryDetail(skillId, newerId),
      ]);
      setCompare({
        left: {
          label: friendlyDate(olderDetail.changedDate),
          sublabel:
            editorLabel(olderDetail) +
            ' • ' +
            numberWithCommas(olderDetail.charCount) +
            ' characters',
          instructions: olderDetail.instructions ?? '',
          description: olderDetail.description ?? '',
        },
        right: {
          label: friendlyDate(newerDetail.changedDate),
          sublabel:
            editorLabel(newerDetail) +
            ' • ' +
            numberWithCommas(newerDetail.charCount) +
            ' characters',
          instructions: newerDetail.instructions ?? '',
          description: newerDetail.description ?? '',
        },
      });
      setPane('compare');
    } catch (err) {
      void message.error(err instanceof Error ? err.message : 'Could not load those versions.');
    } finally {
      setLoading(false);
    }
  }

  // ── Restore (a.k.a. "Use this version") ──────────────────────────────
  function askRestoreConfirm(row: SkillHistoryRow) {
    Modal.confirm({
      title: 'Use this older version?',
      icon: <RollbackOutlined style={{ color: colors.violet[600] }} />,
      width: 480,
      okText: 'Yes, use this version',
      cancelText: 'Cancel',
      okButtonProps: { type: 'primary' },
      content: (
        <div style={{ paddingTop: 8 }}>
          <Text>
            We&apos;ll switch this skill over to the version from{' '}
            <Text strong>{friendlyDate(row.changedDate)}</Text>
            {row.changedByName ? (
              <>
                , saved by <Text strong>{row.changedByName}</Text>
              </>
            ) : null}
            .
          </Text>
          <div
            style={{
              marginTop: 12,
              padding: '10px 12px',
              borderRadius: 8,
              background: isDark ? colors.slate[800] : colors.slate[50],
              border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
              color: isDark ? colors.slate[500] : colors.slate[600],
              fontSize: 13,
              lineHeight: 1.55,
            }}
          >
            <CheckOutlined style={{ color: colors.green[600], marginRight: 8 }} />
            Your current text won&apos;t be lost — it becomes the newest entry in the
            history, so you can switch back at any time.
          </div>
        </div>
      ),
      onOk: () => doRestore(row),
    });
  }

  async function doRestore(row: SkillHistoryRow) {
    if (skillId == null) return;
    setRestoringId(row.id);
    try {
      const res = await restoreSkillHistory(skillId, row.id);
      void message.success(`Switched to the version from ${friendlyDate(row.changedDate)}.`);
      onRestored?.({
        instructions: res.instructions,
        updatedDate: res.updatedDate,
        version: res.version,
      });
      // Refresh so the new "what was current before the restore" row appears
      // at the top + current body length reflects the restored content.
      await loadAll(skillId);
    } catch (err) {
      void message.error(err instanceof Error ? err.message : 'Could not switch versions.');
    } finally {
      setRestoringId(null);
    }
  }

  // ── Selection helpers ────────────────────────────────────────────────
  function toggleSelected(id: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        if (next.size >= 2) {
          // Drop the oldest pick so the new one wins (less surprising than
          // refusing the click).
          const first = next.values().next().value;
          if (first !== undefined) next.delete(first);
        }
        next.add(id);
      }
      return next;
    });
  }

  // ── Diff data (only when in compare mode + a field is picked) ────────
  const diff: DiffSummary | null = useMemo(() => {
    if (pane !== 'compare' || !compare) return null;
    const oldText = field === 'instructions' ? compare.left.instructions : compare.left.description;
    const newText = field === 'instructions' ? compare.right.instructions : compare.right.description;
    return computeLineDiff(oldText, newText);
  }, [pane, compare, field]);

  if (!open) return null;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="right"
      width="60%"
      destroyOnClose
      closable={false}
      title={null}
      styles={{
        body: { padding: 0, display: 'flex', flexDirection: 'column' },
        header: { display: 'none' },
      }}
    >
      {/* ── Header ──────────────────────────────────────────────────── */}
      <div
        style={{
          padding: '20px 24px 16px',
          borderBottom: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
          background: isDark
            ? `${colors.violet[600]}22`
            : `linear-gradient(135deg, ${colors.violet[100]}, ${colors.slate[50]})`,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
              <HistoryOutlined style={{ fontSize: 18, color: colors.violet[600] }} />
              <Title level={5} style={{ margin: 0 }}>
                Edit history
              </Title>
              <Text
                code
                style={{
                  fontFamily: typography.fontMono,
                  fontSize: 12,
                  background: 'transparent',
                  border: 'none',
                  padding: 0,
                  color: isDark ? colors.slate[300] : colors.slate[700],
                }}
              >
                {currentSkill?.name ?? skillName ?? 'Skill'}
              </Text>
              {currentSkill?.version && (
                <Tag style={{ fontFamily: typography.fontMono, fontSize: 11, margin: 0 }}>
                  current v{currentSkill.version}
                </Tag>
              )}
              {pane === 'list' && rows.length > 0 && (
                <Tag color="purple">
                  {rows.length} {rows.length === 1 ? 'previous version' : 'previous versions'}
                </Tag>
              )}
            </div>
            <Text type="secondary" style={{ fontSize: 13, lineHeight: 1.55 }}>
              {pane === 'list' ? (
                <>
                  Every time you save this skill, the previous text is kept here automatically.
                  Click any version to compare it with the current text or switch back to it —
                  your work is never lost.
                </>
              ) : (
                <>
                  Differences are highlighted:{' '}
                  <Text strong style={{ color: colors.red[600] }}>red</Text> shows what was removed,{' '}
                  <Text strong style={{ color: colors.green[600] }}>green</Text> shows what was added.
                </>
              )}
            </Text>
          </div>
          <Button icon={<CloseOutlined />} onClick={onClose} aria-label="Close history" />
        </div>

        {/* Compare-mode toolbar */}
        {pane === 'compare' && compare && (
          <div
            style={{
              marginTop: 14,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              flexWrap: 'wrap',
            }}
          >
            <Space size={8} wrap>
              <Button icon={<ArrowLeftOutlined />} onClick={() => setPane('list')}>
                Back to history
              </Button>
              <Segmented
                size="small"
                value={field}
                onChange={(v) => setField(v as FieldKey)}
                options={[
                  { label: 'Instructions', value: 'instructions' },
                  { label: 'Description', value: 'description' },
                ]}
              />
            </Space>
            {diff && (
              <Space size={8}>
                {diff.added > 0 && (
                  <Tag color="green" icon={<PlusOutlined />} style={{ borderRadius: 999 }}>
                    {diff.added} {diff.added === 1 ? 'line added' : 'lines added'}
                  </Tag>
                )}
                {diff.removed > 0 && (
                  <Tag color="red" icon={<MinusOutlined />} style={{ borderRadius: 999 }}>
                    {diff.removed} {diff.removed === 1 ? 'line removed' : 'lines removed'}
                  </Tag>
                )}
                {diff.added === 0 && diff.removed === 0 && (
                  <Tag color="default" style={{ borderRadius: 999 }}>
                    Identical text
                  </Tag>
                )}
              </Space>
            )}
          </div>
        )}
      </div>

      {/* ── Body ─────────────────────────────────────────────────────── */}
      <div style={{ flex: 1, overflow: 'auto', padding: pane === 'list' ? '20px 24px' : 0 }}>
        {pane === 'list' && (
          <ListPane
            loading={loading}
            rows={rows}
            loadError={loadError}
            currentLen={currentSkill?.instructions?.length ?? 0}
            selected={selected}
            restoringId={restoringId}
            isDark={isDark}
            deltaFor={deltaFor}
            onToggleSelect={toggleSelected}
            onCompareCurrent={compareWithCurrent}
            onRestore={askRestoreConfirm}
            onRetry={() => skillId != null && void loadAll(skillId)}
          />
        )}
        {pane === 'compare' && compare && diff && (
          <ComparePane left={compare.left} right={compare.right} diff={diff} isDark={isDark} />
        )}
      </div>

      {/* ── Footer (list mode only) ─────────────────────────────────── */}
      {pane === 'list' && (
        <div
          style={{
            padding: '12px 24px',
            borderTop: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
            background: isDark ? colors.slate[800] : '#fff',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
            <Text type="secondary" style={{ fontSize: 12.5 }}>
              {selected.size === 0 && 'Tip: tick two versions to compare them side by side.'}
              {selected.size === 1 && 'Tick one more version to compare them.'}
              {selected.size === 2 && 'Two versions selected — ready to compare.'}
            </Text>
            <Space>
              <Button onClick={() => setSelected(new Set())} disabled={selected.size === 0}>
                Clear selection
              </Button>
              <Button
                type="primary"
                icon={<SwapOutlined />}
                disabled={selected.size !== 2}
                onClick={compareSelectedTwo}
              >
                Compare {selected.size === 2 ? '2 selected' : 'selected'}
              </Button>
            </Space>
          </div>
        </div>
      )}
    </Drawer>
  );
});

// ─────────────────────────────────────────────────────────────────────────────
// List pane — timeline of version cards
// ─────────────────────────────────────────────────────────────────────────────

interface ListPaneProps {
  loading: boolean;
  rows: SkillHistoryRow[];
  loadError: string | null;
  currentLen: number;
  selected: Set<number>;
  restoringId: number | null;
  isDark: boolean;
  deltaFor: Map<number, number>;
  onToggleSelect: (id: number) => void;
  onCompareCurrent: (row: SkillHistoryRow) => void;
  onRestore: (row: SkillHistoryRow) => void;
  onRetry: () => void;
}

function ListPane({
  loading,
  rows,
  loadError,
  currentLen,
  selected,
  restoringId,
  isDark,
  deltaFor,
  onToggleSelect,
  onCompareCurrent,
  onRestore,
  onRetry,
}: ListPaneProps) {
  if (loading && rows.length === 0) {
    return (
      <div>
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            style={{
              padding: 16,
              marginBottom: 12,
              borderRadius: 12,
              background: isDark ? colors.slate[800] : '#fff',
              border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
            }}
          >
            <Skeleton active paragraph={{ rows: 2 }} />
          </div>
        ))}
      </div>
    );
  }

  if (loadError) {
    return (
      <Alert
        type="error"
        showIcon
        message="We couldn't load the version history"
        description={
          <div>
            <div style={{ marginBottom: 10 }}>{loadError}</div>
            <Button onClick={onRetry}>Try again</Button>
          </div>
        }
      />
    );
  }

  if (rows.length === 0) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 60 }}>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <div style={{ maxWidth: 380, margin: '0 auto' }}>
              <Text strong style={{ fontSize: 15, display: 'block', marginBottom: 6 }}>
                No previous versions yet
              </Text>
              <Text type="secondary" style={{ fontSize: 13, lineHeight: 1.55 }}>
                As you edit and save this skill, your previous text will be saved here automatically.
                You&apos;ll always be able to come back to an earlier version.
              </Text>
            </div>
          }
        />
      </div>
    );
  }

  return (
    <div>
      {/* Hero strip — current state context, so users know what they're comparing AGAINST */}
      <div
        style={{
          padding: '12px 14px',
          marginBottom: 16,
          borderRadius: 12,
          background: isDark ? `${colors.green[700]}22` : colors.green[100],
          border: `1px solid ${isDark ? colors.green[700] : colors.green[500]}66`,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <CheckOutlined style={{ color: colors.green[600], fontSize: 16 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text strong>This is what the skill uses right now</Text>
          <div style={{ marginTop: 2 }}>
            <Text type="secondary" style={{ fontSize: 12.5 }}>
              {numberWithCommas(currentLen)} characters in the current instructions.
            </Text>
          </div>
        </div>
      </div>

      {rows.map((row, idx) => {
        const isSelected = selected.has(row.id);
        const delta = deltaFor.get(row.id) ?? 0;
        const isRestoring = restoringId === row.id;
        const isOldest = idx === rows.length - 1;
        return (
          <div
            key={row.id}
            style={{
              position: 'relative',
              padding: 14,
              marginBottom: 10,
              borderRadius: 12,
              background: isSelected
                ? isDark
                  ? `${colors.violet[600]}22`
                  : colors.violet[100]
                : isDark
                ? colors.slate[800]
                : '#fff',
              border: `1px solid ${
                isSelected
                  ? colors.violet[500]
                  : isDark
                  ? colors.slate[700]
                  : colors.slate[200]
              }`,
              transition: 'background 0.15s, border-color 0.15s',
            }}
          >
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <Tooltip title={isSelected ? 'Unselect this version' : 'Select to compare'}>
                <label
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 22,
                    height: 22,
                    marginTop: 2,
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => onToggleSelect(row.id)}
                    style={{ width: 18, height: 18, cursor: 'pointer', accentColor: colors.violet[600] }}
                  />
                </label>
              </Tooltip>

              <Avatar
                size={36}
                style={{
                  background: avatarColor(row.changedByName ?? String(row.changedBy)),
                  flexShrink: 0,
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                {initials(row.changedByName, row.changedBy)}
              </Avatar>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    rowGap: 4,
                    columnGap: 10,
                    alignItems: 'baseline',
                    marginBottom: 4,
                  }}
                >
                  <Text strong style={{ fontSize: 14 }}>
                    {friendlyDate(row.changedDate)}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    by {row.changedByName ?? (row.changedBy === 0 ? 'system' : `employee ${row.changedBy}`)}
                  </Text>
                  {row.version && (
                    <Tag style={{ fontFamily: typography.fontMono, fontSize: 11, marginLeft: 0 }}>
                      v{row.version}
                    </Tag>
                  )}
                  {isOldest && (
                    <Tag color="default" style={{ fontSize: 11 }}>
                      Earliest saved version
                    </Tag>
                  )}
                </div>
                <div style={{ marginBottom: 10 }}>
                  <Tag
                    color={delta > 0 ? 'green' : delta < 0 ? 'red' : 'default'}
                    icon={delta > 0 ? <PlusOutlined /> : delta < 0 ? <MinusOutlined /> : null}
                    style={{ borderRadius: 999, fontSize: 11.5 }}
                  >
                    {delta === 0
                      ? 'No size change'
                      : delta > 0
                      ? `Added ${numberWithCommas(delta)} characters`
                      : `Trimmed ${numberWithCommas(-delta)} characters`}
                  </Tag>
                  <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
                    <ClockCircleOutlined /> {dayjs(row.changedDate).format('MMM D, YYYY · h:mm A')}
                  </Text>
                </div>

                <Space size={8} wrap>
                  <Button size="small" icon={<EyeOutlined />} onClick={() => onCompareCurrent(row)}>
                    Compare with current
                  </Button>
                  <Button
                    size="small"
                    type="primary"
                    icon={<RollbackOutlined />}
                    loading={isRestoring}
                    onClick={() => onRestore(row)}
                  >
                    Use this version
                  </Button>
                </Space>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Compare pane — side-by-side line diff with synchronized scroll
// ─────────────────────────────────────────────────────────────────────────────

interface ComparePaneProps {
  left: { label: string; sublabel: string };
  right: { label: string; sublabel: string };
  diff: DiffSummary;
  isDark: boolean;
}

function ComparePane({ left, right, diff, isDark }: ComparePaneProps) {
  const leftRef = useRef<HTMLDivElement | null>(null);
  const rightRef = useRef<HTMLDivElement | null>(null);
  const syncingRef = useRef(false);

  function makeScrollHandler(self: 'L' | 'R') {
    return (e: React.UIEvent<HTMLDivElement>) => {
      if (syncingRef.current) return;
      const src = e.currentTarget;
      const dst = self === 'L' ? rightRef.current : leftRef.current;
      if (!dst) return;
      syncingRef.current = true;
      dst.scrollTop = src.scrollTop;
      requestAnimationFrame(() => {
        syncingRef.current = false;
      });
    };
  }

  const colHeader = (label: string, sub: string, kind: 'older' | 'newer') => (
    <div
      style={{
        padding: '10px 12px',
        borderBottom: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
        background: isDark ? colors.slate[800] : colors.slate[50],
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
        <Tag color={kind === 'older' ? 'default' : 'blue'} style={{ marginRight: 0 }}>
          {kind === 'older' ? 'Older' : 'Newer'}
        </Tag>
        <Text strong style={{ fontSize: 13.5 }}>
          {label}
        </Text>
      </div>
      <Text type="secondary" style={{ fontSize: 12 }}>
        {sub}
      </Text>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {diff.added === 0 && diff.removed === 0 && (
        <div style={{ padding: 16 }}>
          <Alert
            type="info"
            showIcon
            message="These two versions have identical text."
            description="Nothing was added or removed between them. The save may have only changed metadata or whitespace."
          />
        </div>
      )}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          flex: 1,
          minHeight: 0,
          borderTop: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
        }}
      >
        {/* LEFT (older) */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            borderRight: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
          }}
        >
          {colHeader(left.label, left.sublabel, 'older')}
          <div
            ref={leftRef}
            onScroll={makeScrollHandler('L')}
            style={{
              flex: 1,
              minHeight: 0,
              overflow: 'auto',
              fontFamily: typography.fontMono,
              fontSize: 12.5,
              lineHeight: 1.6,
              padding: '8px 0',
              background: isDark ? colors.slate[900] : '#fff',
            }}
          >
            <DiffColumn rows={diff.rows} side="left" isDark={isDark} />
          </div>
        </div>
        {/* RIGHT (newer) */}
        <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          {colHeader(right.label, right.sublabel, 'newer')}
          <div
            ref={rightRef}
            onScroll={makeScrollHandler('R')}
            style={{
              flex: 1,
              minHeight: 0,
              overflow: 'auto',
              fontFamily: typography.fontMono,
              fontSize: 12.5,
              lineHeight: 1.6,
              padding: '8px 0',
              background: isDark ? colors.slate[900] : '#fff',
            }}
          >
            <DiffColumn rows={diff.rows} side="right" isDark={isDark} />
          </div>
        </div>
      </div>
    </div>
  );
}

interface DiffColumnProps {
  rows: DiffRow[];
  side: 'left' | 'right';
  isDark: boolean;
}

function DiffColumn({ rows, side, isDark }: DiffColumnProps) {
  const removedBg = isDark ? '#3F1D1D' : '#FFF1F1';
  const removedBgEdge = isDark ? '#7F1D1D' : '#FCA5A5';
  const addedBg = isDark ? '#0F2E1F' : '#F0FDF4';
  const addedBgEdge = isDark ? '#065F46' : '#86EFAC';

  return (
    <div>
      {rows.map((r, idx) => {
        const showOnThisSide =
          r.op === 'equal' || (side === 'left' ? r.op === 'removed' : r.op === 'added');
        const text = side === 'left' ? r.left : r.right;
        const lineNo = side === 'left' ? r.leftLineNo : r.rightLineNo;
        const isChange = showOnThisSide && (r.op === 'removed' || r.op === 'added');
        const bg = isChange ? (r.op === 'removed' ? removedBg : addedBg) : 'transparent';
        const edge = isChange ? (r.op === 'removed' ? removedBgEdge : addedBgEdge) : 'transparent';

        return (
          <div
            key={`${side}-${idx}`}
            style={{
              display: 'grid',
              gridTemplateColumns: '46px 1fr',
              background: bg,
              borderLeft: `3px solid ${edge}`,
              minHeight: 22,
            }}
          >
            <div
              style={{
                textAlign: 'right',
                paddingRight: 10,
                color: isDark ? colors.slate[500] : colors.slate[500],
                userSelect: 'none',
                fontSize: 11,
                paddingTop: 1,
              }}
            >
              {lineNo > 0 ? lineNo : ''}
            </div>
            <pre
              style={{
                margin: 0,
                padding: '0 12px 0 0',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontFamily: typography.fontMono,
                color: isDark ? colors.slate[100] : colors.slate[700],
              }}
            >
              {showOnThisSide ? text || ' ' : ' '}
            </pre>
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function friendlyDate(iso: string): string {
  const d = dayjs(iso);
  const now = dayjs();
  const hoursAgo = now.diff(d, 'hour');
  if (hoursAgo < 1) return d.fromNow();
  if (hoursAgo < 24) return d.fromNow();
  if (now.diff(d, 'day') === 1) return `Yesterday at ${d.format('h:mm A')}`;
  if (now.diff(d, 'day') < 7) return d.format('dddd [at] h:mm A');
  return d.format('MMM D [at] h:mm A');
}

function editorLabel(row: SkillHistoryRow): string {
  if (row.changedByName) return row.changedByName;
  if (row.changedBy === 0) return 'system';
  return `employee ${row.changedBy}`;
}

function initials(name: string | null | undefined, employeeId: number): string {
  if (!name) return employeeId === 0 ? 'SY' : '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function avatarColor(seed: string): string {
  const palette = [
    colors.ink[600],
    colors.violet[600],
    colors.green[600],
    colors.amber[600],
    colors.slate[600],
  ];
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  return palette[hash % palette.length];
}

function numberWithCommas(n: number): string {
  return n.toLocaleString();
}
